import { faPlus, faUnlock, faCheck } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useCallback, useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { RouteComponentProps } from "react-router-dom";

import { ERouteStaticParams, Routes } from "../../classes/Routes";
import ConfirmBillingModal from "../../components/ConfirmBillingModal/ConfirmBillingModal";
import OfferRequestsInfo from "../../components/OfferRequests/OfferRequestsInfo/OfferRequestsInfo";
import OfferRequestsSearch from "../../components/OfferRequests/OfferRequestsSearch/OfferRequestsSearch";
import OfferRequestTable from "../../components/OfferRequests/OfferRequestsTable/OfferRequestsTable";
import Alert from "../../components/UI/Alert/Alert";
import Button from "../../components/UI/Button/Button";
import ConfirmDialog from "../../components/UI/ConfirmDialog/ConfirmDialog";
import Container from "../../components/UI/Container/Container";
import ModalContext, {
  EModalSize,
} from "../../components/UI/Modal/ModalContext";
import Spinner from "../../components/UI/Spinner/Spinner";
import { useAuthUser } from "../../hooks/useAuthUser";
import { IAppState, IOfferRequestState, IOfferRequest } from "../../interfaces";
import AiOfferModal from "../../components/Offers/AiOfferModal/AiOfferModal";
import { linkHandler } from "../../shared/utility";
import * as actions from "../../store/actions";
import { ETranslation } from "../../translations/translation-keys";
import classes from "./OfferRequests.module.scss";

interface IProps extends RouteComponentProps {
  offerRequestId?: number;
}

const formatDateOnly = (dateStr?: string): string => {
  if (!dateStr) return '-';
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '-';
    return `${date.getDate()}.${date.getMonth() + 1}.${date.getFullYear()}`;
  } catch (e) {
    return '-';
  }
};

const OfferRequests: React.FC<IProps> = ({ history }) => {
  const dispatch = useDispatch();
  const { setModal, closeModal } = useContext(ModalContext);
  const { t } = useTranslation();

  const authUser = useAuthUser();
  const {
    loading,
    error,
    offerRequests,
    addAccessError,
    addAccessLoading,
    addAccessSuccessId,
  } = useSelector<IAppState, IOfferRequestState>((state) => state.offerRequest);

  const setAddAccessModal = useCallback(
    (content: React.ReactNode | null, loading: boolean = false) => {
      setModal({
        isOpen: true,
        title: t(ETranslation.CONFIRM_BUY_TITLE),
        size: EModalSize.SMALL,
        loading,
        content,
      });
    },
    [t, setModal]
  );

  const createAccessModal = (id: string) => {
    const targetOfferRequest = (offerRequests || []).find(
      (item) => item.id?.toString() === id?.toString()
    );

    setAddAccessModal(
      <ConfirmDialog
        body={
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', padding: '0.5rem 0' }}>
            <div style={{
              backgroundColor: '#eff6ff',
              border: '1px solid #bfdbfe',
              borderRadius: '10px',
              padding: '1.25rem',
              display: 'flex',
              gap: '12px',
              alignItems: 'flex-start',
              textAlign: 'left'
            }}>
              <div style={{
                color: '#2563eb',
                fontSize: '1.25rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#dbeafe',
                borderRadius: '8px',
                width: '40px',
                height: '40px',
                flexShrink: 0
              }}>
                <FontAwesomeIcon icon={faUnlock} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <strong style={{ color: '#1e3a8a', fontSize: '0.95rem' }}>Avaa tarjouspyynnön täydelliset tiedot</strong>
                <p style={{ margin: 0, color: '#1e40af', fontSize: '0.85rem', lineHeight: '1.5' }}>
                  Saat heti käyttöösi asiakkaan yhteystiedot ja kohteen kaikki perustiedot. Tiedot ovat käytössäsi ilman aikarajoitusta.
                </p>
              </div>
            </div>

            <div style={{
              backgroundColor: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: '10px',
              padding: '1rem 1.25rem',
              display: 'flex',
              flexDirection: 'column',
              gap: '0.6rem',
              textAlign: 'left'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem', color: '#475569' }}>
                <FontAwesomeIcon icon={faCheck} style={{ color: '#16a34a', fontSize: '0.9rem', flexShrink: 0 }} /> Asiakkaan täydelliset yhteystiedot (nimi, puhelin, sähköposti)
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem', color: '#475569' }}>
                <FontAwesomeIcon icon={faCheck} style={{ color: '#16a34a', fontSize: '0.9rem', flexShrink: 0 }} /> Kohdetiedot karttalinkkeineen (osoite ja rakennustiedot)
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem', color: '#475569' }}>
                <FontAwesomeIcon icon={faCheck} style={{ color: '#16a34a', fontSize: '0.9rem', flexShrink: 0 }} /> Asiakkaan syöttämät lisätiedot ja tarkennukset
              </div>
            </div>

            {targetOfferRequest && (
              <div style={{
                backgroundColor: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: '10px',
                padding: '1rem 1.25rem',
                display: 'flex',
                flexDirection: 'column',
                gap: '0.5rem',
                textAlign: 'left',
                fontSize: '0.875rem',
                lineHeight: 1.5,
                boxSizing: 'border-box',
                width: '100%'
              }}>
                <div style={{ fontWeight: 600, color: '#1e293b' }}>
                  Tarjouspyynnön kilpailutilanne:
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', color: '#334155' }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                    <span style={{ color: '#64748b', fontSize: '0.75rem', lineHeight: 1 }}>•</span>
                    <span>
                      <strong>Tarjouspyyntö luotu:</strong> {formatDateOnly(targetOfferRequest.created)}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                    <span style={{ color: '#64748b', fontSize: '0.75rem', lineHeight: 1 }}>•</span>
                    <span>
                      <strong>Tarjouspyyntöä katselleet yritykset:</strong> {targetOfferRequest.viewCount ?? 0}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                    <span style={{ color: '#64748b', fontSize: '0.75rem', lineHeight: 1 }}>•</span>
                    <span>
                      <strong>Tarjouksia tehty (HankiX:n kautta):</strong> {targetOfferRequest.offerCount ?? 0}
                    </span>
                  </div>
                </div>
              </div>
            )}

            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: '#fffbeb',
              border: '1px solid #fde68a',
              borderRadius: '10px',
              padding: '1rem 1.25rem',
              marginTop: '0.25rem',
              textAlign: 'left'
            }}>
              <span style={{ fontWeight: 600, color: '#92400e', fontSize: '0.95rem' }}>Avaushinta</span>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                <span style={{ fontSize: '1.5rem', fontWeight: 800, color: '#b45309', lineHeight: '1' }}>19 €</span>
                <span style={{ fontSize: '0.75rem', color: '#b45309', fontWeight: 500 }}>+ alv</span>
              </div>
            </div>

            <div style={{
              fontSize: '1rem',
              fontWeight: 600,
              color: '#0f172a',
              textAlign: 'center',
              marginTop: '0.5rem',
              borderTop: '1px solid #f1f5f9',
              paddingTop: '1rem'
            }}>
              Haluatko lunastaa tämän tarjouspyynnön tiedot?
            </div>
          </div>
        }
        onConfirm={() => {
          dispatch(actions.addAccessOfferRequest(id));
        }}
        onCancel={closeModal}
      />
    );
  };

  const showOffer = (
    event: React.MouseEvent<HTMLTableRowElement | HTMLButtonElement>,
    id: string
  ) => {
    if (authUser?.isMicro) {
      if (authUser?.hasOfferRequestAccess(id)) {
        linkHandler(event, Routes.OFFER_REQUEST(id), history);
      } else if (authUser.organization.isBillingConfirmed) {
        createAccessModal(id);
      } else {
        setModal({
          isOpen: true,
          content: (
            <ConfirmBillingModal
              onSuccess={() => {
                createAccessModal(id);
              }}
            />
          ),
          size: EModalSize.MEDIUM,
          title: t(ETranslation.SERVICEPROVIDER_INVOICE_CONFIRM),
        });
      }
    } else {
      linkHandler(event, Routes.OFFER_REQUEST(id), history);
    }
  };

  useEffect(() => {
    if (addAccessLoading) setAddAccessModal(null, true);
  }, [addAccessLoading, setAddAccessModal]);

  useEffect(() => {
    if (addAccessError)
      setAddAccessModal(
        <Alert noHorizontalMargin>
          {t(ETranslation.OFFER_REQUEST_ADD_ACCESS_ERROR)}
        </Alert>
      );
  }, [addAccessError, setAddAccessModal, t]);

  useEffect(() => {
    if (addAccessSuccessId) {
      closeModal();
      dispatch(actions.addAuthUserOfferRequestAccess(addAccessSuccessId));
      history.push(Routes.OFFER_REQUEST(addAccessSuccessId));
    }
  }, [addAccessSuccessId, history, dispatch, closeModal]);

  useEffect(() => {
    return () => {
      dispatch(actions.addAccessOfferRequestClear());
    };
  }, [dispatch]);

  const [filters, setFilters] = React.useState<any>(null);

  const searchHandler = useCallback(
    (searchParams) => {
      dispatch(actions.listOfferRequests({
        operatorId: searchParams.operatorId,
        limit: searchParams.limit,
        maxDistance: searchParams.maxDistance,
        serviceIds: searchParams.serviceIds && searchParams.serviceIds.length > 0 ? searchParams.serviceIds : undefined
      }));
      setFilters(searchParams);
    },
    [dispatch]
  );

  const filteredOfferRequests = React.useMemo(() => {
    if (!offerRequests) return [];
    if (!filters) return offerRequests;

    return offerRequests.filter((or) => {
      // 1. Service multi-select filter
      if (filters.serviceIds && filters.serviceIds.length > 0) {
        if (!or.service || !filters.serviceIds.map(String).includes(String(or.service.id))) {
          return false;
        }
      }

      // 2. Province multi-select filter
      if (filters.provinces && filters.provinces.length > 0) {
        if (!or.province || !filters.provinces.includes(or.province)) {
          return false;
        }
      }

      // 3. Municipality multi-select filter
      if (filters.municipalities && filters.municipalities.length > 0) {
        if (!or.municipality || !filters.municipalities.includes(or.municipality)) {
          return false;
        }
      }

      // 4. Single municipality or city fallback filter
      if (filters.municipality && filters.municipality.trim().length > 0) {
        const searchMuni = filters.municipality.trim().toLowerCase();
        const orMunicipality = or.municipality ? or.municipality.toLowerCase() : "";
        if (!orMunicipality.includes(searchMuni)) {
          return false;
        }
      }

      if (filters.city && filters.city.trim().length > 0) {
        const searchCity = filters.city.trim().toLowerCase();
        const orCity = or.city ? or.city.toLowerCase() : "";
        const orMunicipality = or.municipality ? or.municipality.toLowerCase() : "";
        if (!orCity.includes(searchCity) && !orMunicipality.includes(searchCity)) {
          return false;
        }
      }

      // 4. Archive filter
      // If showArchived is NOT checked, we hide archived ones (archived === true)
      if (!filters.showArchived && or.archived) {
        return false;
      }

      // 5. Max distance filter (km)
      if (filters.maxDistance && filters.maxDistance.trim().length > 0) {
        const maxDist = parseFloat(filters.maxDistance.trim());
        if (!isNaN(maxDist)) {
          if (or.distanceKm === undefined || or.distanceKm === null || or.distanceKm > maxDist) {
            return false;
          }
        }
      }

      return true;
    });
  }, [offerRequests, filters]);

  const handleOpenAiModal = useCallback(
    (targetOfferRequest: IOfferRequest) => {
      setModal({
        isOpen: true,
        title: `AI-tarjous ja esikatselu: ${targetOfferRequest.service?.name || ''}`,
        size: EModalSize.FULL,
        content: (
          <AiOfferModal
            offerRequest={targetOfferRequest}
            onClose={closeModal}
            onSaved={() => {
              dispatch(
                actions.listOfferRequests(
                  filters
                    ? {
                        operatorId: filters.operatorId,
                        limit: filters.limit,
                        maxDistance: filters.maxDistance,
                        serviceIds:
                          filters.serviceIds && filters.serviceIds.length > 0
                            ? filters.serviceIds
                            : undefined,
                      }
                    : undefined
                )
              );
            }}
          />
        ),
      });
    },
    [setModal, closeModal, dispatch, filters]
  );

  return (
    <Container>
      {error && <Alert>{t(error)}</Alert>}
      <h2>{t(ETranslation.TITLE_OFFERREQUESTS)}</h2>
      {(authUser?.isPrimary || authUser?.isOperator) && (
        <Button
          onClick={() =>
            history.push(Routes.OFFER_REQUEST(ERouteStaticParams.new))
          }
          icon={faPlus}
          style={{ marginBottom: "1rem" }}
        >
          {t(ETranslation.OFFERREQUESTS_ADD_NEW)}
        </Button>
      )}
      <section className={classes.Section}>
        <main style={{ flexGrow: 1 }}>
          <OfferRequestsSearch onSearch={searchHandler} />
          {loading ? (
            <Spinner />
          ) : offerRequests ? (
            <OfferRequestTable
              offerrequests={filteredOfferRequests}
              onClick={showOffer}
              sortable
              authUser={authUser!}
              onOpenAiModal={handleOpenAiModal}
            />
          ) : (
            <p>
              <br />
              {t(ETranslation.NO_OFFERREQUESTS)}
            </p>
          )}
        </main>
        {authUser?.isServiceProvider && (
          <OfferRequestsInfo authUser={authUser} />
        )}
      </section>
    </Container>
  );
};

export default OfferRequests;
